import axios from "axios";
import { LogOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "../App";
import PaymentDialog from "../components/PaymentDialog";
import QuickGraphs from "../components/QuickGraphs";
import AssumptionsSheet from "../components/sheets/AssumptionsSheet";
import BenchmarksSheet from "../components/sheets/BenchmarksSheet";
import DashboardView from "../components/sheets/DashboardView";
import ReadOnlySheet from "../components/sheets/ReadOnlySheet";
import ScenariosSheet from "../components/sheets/ScenariosSheet";
import SetupSheet from "../components/sheets/SetupSheet";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";

const Dashboard = ({ setIsAuthenticated }) => {
  const [selectedTab, setSelectedTab] = useState("Dashboard");
  const [sheetsList, setSheetsList] = useState([]);
  const [editableSheets, setEditableSheets] = useState([]);
  const [user, setUser] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [pendingSheet, setPendingSheet] = useState(null);
  const [paidAccess, setPaidAccess] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const editRestrictedSheets = [
    "Setup",
    "Benchmarks",
    "Assumptions",
    "Scenarios",
  ];

  // Dashboard is always fully accessible
  const alwaysAccessible = ["Dashboard"];
  const verificationStarted = useRef(false);

  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(`${API}/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "active") {
        setPaidAccess(true);
      } else {
        setPaidAccess(false);
      }
    } catch (err) {
      console.error("Failed to check subscription", err);
    }
  }, []);

  const fetchSheetsList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sheets/list`);
      setSheetsList(response.data.sheets);
      setEditableSheets(response.data.editable);
    } catch (error) {
      toast.error("Failed to load sheets list");
    }
  }, []);

  const verifyPaymentIfReturned = useCallback(
    async (paymentId) => {
      // const params = new URLSearchParams(window.location.search);
      // console.log("URL Params:", params.toString());
      // const paymentId = params.get("razorpay_payment_link_id");
      // console.log("Payment ID from URL:", paymentId);

      if (!paymentId) return;

      try {
        const token = localStorage.getItem("token");

        const response = await axios.post(
          `${API}/payments/verify`,
          { payment_id: paymentId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        toast.success("Payment verified! Subscription activated.");
        setPaidAccess(true);

        // Clean URL (remove ?razorpay_payment_id=XYZ)
        setTimeout(() => {
          window.history.replaceState({}, "", "/");
          setVerifyingPayment(false);
          checkSubscriptionStatus();
          fetchSheetsList();
        }, 300);
      } catch (err) {
        console.error("Payment verification failed", err);
        toast.error("Payment verification failed.");

        setVerifyingPayment(false);
        checkSubscriptionStatus();
        fetchSheetsList();
      }
    },
    [checkSubscriptionStatus, fetchSheetsList]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("razorpay_payment_link_id");

    checkSubscriptionStatus();
    fetchSheetsList();

    if (paymentId && !verificationStarted.current) {
      verificationStarted.current = true;
      setVerifyingPayment(true);
      verifyPaymentIfReturned(paymentId);
      return;
    }

    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [checkSubscriptionStatus, fetchSheetsList, verifyPaymentIfReturned]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const handleTabClick = useCallback(
    (sheet) => {
      // Dashboard and edit-restricted sheets are always viewable
      if (
        alwaysAccessible.includes(sheet) ||
        editRestrictedSheets.includes(sheet)
      ) {
        setSelectedTab(sheet);
        return;
      }

      // Other tabs require payment to view
      if (!paidAccess) {
        setPendingSheet(sheet);
        setShowPaymentDialog(true);
      } else {
        setSelectedTab(sheet);
      }
    },
    [paidAccess]
  );

  const renderSheetContent = () => {
    // Check if user has access to view this tab's content
    const canViewContent =
      alwaysAccessible.includes(selectedTab) ||
      editRestrictedSheets.includes(selectedTab) ||
      paidAccess;

    if (!canViewContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="bg-slate-100 p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              Premium Content
            </h3>
            <p className="text-slate-500 mb-4">
              This content requires a paid plan to access.
            </p>
            <Button
              onClick={() => setShowPaymentDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Unlock Access
            </Button>
          </div>
        </div>
      );
    }

    switch (selectedTab) {
      case "Setup":
        return (
          <SetupSheet
            hasAccess={paidAccess}
            onRequestAccess={() => {
              setPendingSheet("Setup");
              setShowPaymentDialog(true);
            }}
          />
        );
      case "Benchmarks":
        return (
          <BenchmarksSheet
            hasAccess={paidAccess}
            onRequestAccess={() => {
              setPendingSheet("Benchmarks");
              setShowPaymentDialog(true);
            }}
          />
        );
      case "Assumptions":
        return (
          <AssumptionsSheet
            hasAccess={paidAccess}
            onRequestAccess={() => {
              setPendingSheet("Assumptions");
              setShowPaymentDialog(true);
            }}
          />
        );
      case "Scenarios":
        return (
          <ScenariosSheet
            hasAccess={paidAccess}
            onRequestAccess={() => {
              setPendingSheet("Scenarios");
              setShowPaymentDialog(true);
            }}
          />
        );
      case "Dashboard":
        return <DashboardView />;
      default:
        return <ReadOnlySheet sheetName={selectedTab} />;
    }
  };

  if (verifyingPayment) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600 text-sm">
            Verifying payment, please wait...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen bg-slate-50"
      data-testid="dashboard-container"
    >
      {/* Left Sidebar - Tabs */}
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <h1
            className="text-2xl font-bold text-slate-800"
            style={{ fontFamily: "Space Grotesk" }}
          >
            AOP Builder
          </h1>
          <p className="text-sm text-slate-500 mt-1">Scenario Analysis</p>
        </div>

        <ScrollArea className="flex-1 py-4">
          <div className="space-y-1 px-3">
            {/* {sheetsList.map((sheet) => { */}
            {[...sheetsList].reverse().map((sheet) => {
              const isLocked =
                !alwaysAccessible.includes(sheet) &&
                !editRestrictedSheets.includes(sheet) &&
                !paidAccess;

              return (
                <button
                  key={sheet}
                  data-testid={`tab-${sheet.toLowerCase()}`}
                  onClick={() => handleTabClick(sheet)}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors relative ${
                    selectedTab === sheet
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <span className="flex items-center justify-between">
                    <span>{sheet.replace(/_/g, " ")}</span>
                    {isLocked && (
                      <svg
                        className="w-4 h-4 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    )}
                  </span>
                  {editableSheets.includes(sheet) && !isLocked && (
                    <span className="ml-2 text-xs text-blue-500">• Edit</span>
                  )}
                </button>
              );
            })}
          </div>
        </ScrollArea>

        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="text-xs text-slate-500 px-2 mb-2">
            {user?.name && (
              <div className="font-medium text-slate-700">{user.name}</div>
            )}
            <div className="truncate">{user?.email}</div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            size="sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2
              className="text-xl font-semibold text-slate-800"
              style={{ fontFamily: "Space Grotesk" }}
            >
              {selectedTab.replace(/_/g, " ")}
            </h2>
            {editableSheets.includes(selectedTab) && paidAccess && (
              <p className="text-sm text-slate-500 mt-0.5">
                Editable Sheet - Auto-saves on change
              </p>
            )}
            {editRestrictedSheets.includes(selectedTab) && !paidAccess && (
              <p className="text-sm text-amber-600 mt-0.5">
                View-only - Payment required to edit
              </p>
            )}
          </div>
          <div className="text-xs text-slate-400">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
            Live sync enabled
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-auto p-6">
            {!showPaymentDialog && renderSheetContent()}
          </div>

          {/* Right Sidebar - Quick Graphs */}
          {!showPaymentDialog && selectedTab !== "Dashboard" && <QuickGraphs />}
        </div>
      </div>

      {!paidAccess && (
        <PaymentDialog
          open={showPaymentDialog}
          loading={paymentLoading}
          onClose={() => setShowPaymentDialog(false)}
          // onProceed={async () => {
          //   // open popup immediately to avoid browser blocking
          //   const popup = window.open("", "_blank");

          //   try {
          //     const token = localStorage.getItem("token");

          //     const response = await axios.post(
          //       `${API}/payments/create-link`,
          //       { amount: 300, redirect_url: "http://localhost:3000/" },
          //       { headers: { Authorization: `Bearer ${token}` } }
          //     );

          //     const paymentUrl = response.data.short_url;

          //     // redirect inside popup
          //     popup.location.href = paymentUrl;

          //     toast.info("Redirecting to payment...");
          //   } catch (error) {
          //     popup.close();
          //     toast.error(error.response?.data?.detail || "Failed to start payment");
          //   }
          // }}
          onProceed={async () => {
            if (paymentLoading) return;
            setPaymentLoading(true);

            try {
              const token = localStorage.getItem("token");
              // const PAYMENT_REDIRECT_URL =
              //   process.env.REACT_APP_PAYMENT_REDIRECT_URL;
              // console.log("TEST ENV:", PAYMENT_REDIRECT_URL);

              const response = await axios.post(
                `${API}/payments/create-link`,
                { amount: 300, redirect_url: window.location.href },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              const paymentUrl = response.data.short_url;
              window.location.href = paymentUrl;

              toast.info("Redirecting to payment...");
            } catch (error) {
              toast.error(
                error.response?.data?.detail || "Failed to start payment"
              );
              setPaymentLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
